import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import authAPI from "../../../services/authApi";
import ClientProfileSinglePage from "./ClientProfileSinglePage";
import { useHubBasePath, usePatientsBase } from "../../../utils/hubPaths";
import { EMPTY_EDIT, patientToEdit } from "./patientConstants";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { HubScreen } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import s from "../clinic.module.css";
import { normalizePhoneRu } from "../../../utils/phoneMask";
import {
  MANUAL_PORTAL_INVITE_MSG,
  promptPortalInviteCopy,
  showPortalInviteFeedback,
} from "../../../utils/portalInviteFeedback";
import { useToast, Modal, Button, Input } from "../../ui";
import RegistrationModerationSheet from "./RegistrationModerationSheet";
import L from "../../../constants/baysportLabels";

export default function AdminPatientProfile({ staffMode = false }) {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const hubBase = useHubBasePath();
  const patientsBase = usePatientsBase();
  const toast = useToast();
  const isApp = useAdminHubUi();
  const isDesktop = !useIsMobile(768);

  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const subdomain = useSelector((state) => state.auth.tenant?.subdomain);
  const [competitionPickerOpen, setCompetitionPickerOpen] = useState(false);
  const [competitionEvents, setCompetitionEvents] = useState([]);
  const [competitionLoading, setCompetitionLoading] = useState(false);
  const [activationInviteUrl, setActivationInviteUrl] = useState("");
  const [moderationStatus, setModerationStatus] = useState(null);
  const [moderationBusy, setModerationBusy] = useState(false);

  const loadProfile = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    clinicAPI
      .getSportClientProfile(patientId)
      .then((r) => {
        setProfile(r);
        setEdit(patientToEdit(r.client));
      })
      .catch(() => {
        clinicAPI
          .getPatient(patientId)
          .then((r) => {
            setProfile({
              client: r.patient,
              groups: [],
              memberships: [],
              payments: [],
              documents: [],
            });
            setEdit(patientToEdit(r.patient));
          })
          .catch((e) => {
            toast.error(e.response?.data?.error || "Клиент не найден");
            navigate(patientsBase);
          });
      })
      .finally(() => setLoading(false));
  }, [patientId, navigate, toast]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const savePatient = async (e) => {
    e?.preventDefault?.();
    try {
      await clinicAPI.updatePatient(patientId, edit);
      toast.success("Сохранено");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const savePortalEmail = async (e) => {
    e?.preventDefault?.();
    setSavingEmail(true);
    try {
      await clinicAPI.updatePatient(patientId, {
        phone: normalizePhoneRu(edit.phone) || null,
        email: edit.email?.trim() || null,
      });
      toast.success("Контакты для входа сохранены");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSavingEmail(false);
    }
  };

  const setPortalPassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    setSettingPassword(true);
    try {
      await authAPI.adminSetUserPassword(patientId, newPassword);
      setNewPassword("");
      toast.success("Пароль задан");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSettingPassword(false);
    }
  };

  const archiveClient = async () => {
    if (!window.confirm("Отправить клиента в архив? История сохранится.")) return;
    try {
      await clinicAPI.updatePatient(patientId, {
        ...edit,
        is_archived: true,
      });
      toast.success("Клиент в архиве");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const restoreClient = async () => {
    try {
      await clinicAPI.updatePatient(patientId, {
        ...edit,
        is_archived: false,
      });
      toast.success("Клиент восстановлен");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const softDeleteClient = async () => {
    const name = profile?.client?.full_name || "клиента";
    if (
      !window.confirm(
        `Удалить «${name}» из списка клиентов?\n\nКлиент исчезнет из раздела «Клиенты». История оплат и посещений сохранится для статистики.\nТелефон и email освободятся — можно будет зарегистрироваться заново с этими контактами.`,
      )
    ) {
      return;
    }
    try {
      await clinicAPI.softDeleteClient(patientId);
      toast.success("Клиент удалён из списка");
      navigate(patientsBase);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось удалить");
    }
  };

  const writeMessage = () => {
    navigate(`${hubBase}/chat/client/${patientId}`, {
      state: { clientName: patient?.full_name || "" },
    });
  };

  const openCompetitionPicker = async () => {
    setCompetitionPickerOpen(true);
    setCompetitionLoading(true);
    try {
      const from = new Date().toISOString().slice(0, 10);
      const toDate = new Date();
      toDate.setMonth(toDate.getMonth() + 6);
      const to = toDate.toISOString().slice(0, 10);
      const data = await clinicAPI.getTrainingSessions({ from, to });
      const list = (data.sessions || []).filter(
        (s) => s.session_kind === "event" && s.event_kind === "competition",
      );
      setCompetitionEvents(list);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки событий");
      setCompetitionEvents([]);
    } finally {
      setCompetitionLoading(false);
    }
  };

  const addToCompetition = async (sessionId) => {
    try {
      await clinicAPI.addSessionRegistration(sessionId, {
        patient_id: patientId,
        status: "approved",
      });
      toast.success("Клиент добавлен в список");
      setCompetitionPickerOpen(false);
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const moderateRegistration = (status) => {
    setModerationStatus(status);
  };

  const confirmModeration = async () => {
    if (!moderationStatus || moderationBusy) return;
    const status = moderationStatus;
    setModerationBusy(true);
    try {
      const r = await clinicAPI.moderateClientRegistration(patientId, status);
      if (status === "approved") {
        setActivationInviteUrl("");
        const { needsManualCopy, inviteUrl } = await showPortalInviteFeedback(
          toast,
          r,
          "Регистрация подтверждена",
        );
        if (inviteUrl && !r.invite_email_sent && !r.invite_sms_sent) {
          setActivationInviteUrl(inviteUrl);
        }
        if (needsManualCopy) promptPortalInviteCopy(inviteUrl);
      } else {
        setActivationInviteUrl("");
        toast.success(r.message || "Заявка отклонена");
      }
      setModerationStatus(null);
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось обработать заявку");
    } finally {
      setModerationBusy(false);
    }
  };

  const copyActivationLink = async () => {
    if (!activationInviteUrl) return;
    try {
      await navigator.clipboard.writeText(activationInviteUrl);
      toast.success("Ссылка активации скопирована");
    } catch {
      promptPortalInviteCopy(activationInviteUrl);
    }
  };

  if (loading && !profile) {
    if (isApp) {
      return (
        <HubScreen
          title="Клиент"
          onBack={() => navigate(patientsBase)}
          backLabel="К клиентам"
        >
          <p className={aa.hubHint}>Загрузка…</p>
        </HubScreen>
      );
    }
    return (
      <div className={s.patientPage}>
        <p style={{ color: "var(--color-text-muted)" }}>Загрузка…</p>
      </div>
    );
  }

  const patient = profile?.client;
  const sport = patient?.sport;
  const pendingRegistration = sport?.registration_status === "pending";

  const moderationBanner = pendingRegistration ? (
    <div className={s.registrationModerationBanner}>
      <p>Заявка на регистрацию ожидает подтверждения</p>
      <div className={s.membershipPayFormBtns}>
        <Button type="button" onClick={() => moderateRegistration("approved")}>
          Подтвердить
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => moderateRegistration("rejected")}
        >
          Отклонить
        </Button>
      </div>
    </div>
  ) : null;

  const activationLinkBanner = activationInviteUrl ? (
    <div className={s.registrationModerationBanner}>
      <p>{MANUAL_PORTAL_INVITE_MSG}</p>
      <div className={s.inlineFieldRow}>
        <Input value={activationInviteUrl} readOnly />
        <Button type="button" variant="secondary" onClick={copyActivationLink}>
          Копировать
        </Button>
      </div>
    </div>
  ) : null;

  const profileBody = (
    <>
      {moderationBanner}
      {activationLinkBanner}
      <ClientProfileSinglePage
        patientId={patientId}
        patient={patient}
        profile={profile}
        edit={edit}
        setEdit={setEdit}
        sport={sport}
        onSave={savePatient}
        onArchive={staffMode ? undefined : archiveClient}
        onRestore={staffMode ? undefined : restoreClient}
        onSoftDelete={staffMode ? undefined : softDeleteClient}
        onReload={loadProfile}
        writeMessage={writeMessage}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSavePortalEmail={savePortalEmail}
        onSetPortalPassword={setPortalPassword}
        savingPortalEmail={savingEmail}
        settingPortalPassword={settingPassword}
        onAddToCompetition={openCompetitionPicker}
      />
    </>
  );

  const competitionPicker = (
    <Modal
      open={competitionPickerOpen}
      onClose={() => setCompetitionPickerOpen(false)}
      title="Добавить на соревнование"
    >
      {competitionLoading ? (
        <p className={s.clientTabMuted}>Загрузка…</p>
      ) : competitionEvents.length === 0 ? (
        <p className={s.clientTabMuted}>
          Нет предстоящих соревнований. Создайте событие в календаре.
        </p>
      ) : (
        <ul className={s.competitionRegList}>
          {competitionEvents.map((ev) => (
            <li key={ev.id} className={s.competitionRegItem}>
              <div>
                <strong>{ev.title || "Соревнование"}</strong>
                <span className={s.clientTabMuted}>
                  {ev.start_datetime
                    ? new Date(ev.start_datetime).toLocaleDateString("ru-RU")
                    : ""}
                </span>
              </div>
              <Button type="button" size="sm" onClick={() => addToCompetition(ev.id)}>
                Добавить
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(patientsBase);
  };

  const moderationSheet = (
    <RegistrationModerationSheet
      open={Boolean(moderationStatus)}
      client={patient}
      status={moderationStatus}
      busy={moderationBusy}
      onClose={() => !moderationBusy && setModerationStatus(null)}
      onConfirm={confirmModeration}
    />
  );

  if (isApp) {
    return (
      <HubScreen
        title={patient?.full_name || "Клиент"}
        onBack={handleBack}
        backLabel="Назад"
        bodyClassName={aa.hubPageBodyFlush}
      >
        {profileBody}
        {moderationSheet}
        {competitionPicker}
      </HubScreen>
    );
  }

  return (
    <div className={isDesktop ? md.desktopClientProfilePage : s.patientPage}>
      {isDesktop ? (
        <button
          type="button"
          className={md.desktopHubBack}
          onClick={() => navigate(patientsBase)}
        >
          ← Назад к клиентам
        </button>
      ) : (
        <nav className={s.patientBreadcrumbs} aria-label="Навигация">
          <Link to={patientsBase}>{L.clients}</Link>
          <span aria-hidden>/</span>
          <span>{patient?.full_name || "Клиент"}</span>
        </nav>
      )}
      {profileBody}
      {moderationSheet}
      {competitionPicker}
    </div>
  );
}
